<template>
  <div class="home-page">
    <!-- 顶部 Banner 轮播 -->
    <section class="hero-slider" @mouseenter="pauseAuto" @mouseleave="resumeAuto">
      <div class="hero-slides">
        <div v-for="(b,idx) in banners" :key="idx" class="hero-slide" :class="{ 'is-active': idx === activeBanner }" :style="slideStyle(b)">
          <div class="hero-overlay" v-if="idx === activeBanner">
            <h2 class="hero-banner-title">{{ b.title }}</h2>
            <p class="hero-banner-sub" v-if="b.summary">{{ b.summary }}</p>
          </div>
        </div>
      </div>
      <button class="hero-arrow hero-arrow--left" @click="prevBanner" aria-label="上一张">
        <span>&lsaquo;</span>
      </button>
      <button class="hero-arrow hero-arrow--right" @click="nextBanner" aria-label="下一张">
        <span>&rsaquo;</span>
      </button>
      <div class="hero-dots">
        <button v-for="(b,idx) in banners" :key="'dot-'+idx" :class="['dot',{active: idx===activeBanner}]" @click="gotoBanner(idx)" :aria-label="'跳转第'+(idx+1)+'张'"> </button>
      </div>
    </section>
    
    
    <!-- 新闻资讯 -->
    <section class="news-section">
      <div class="content-wrapper">
        <h2 class="section-title section-title-accent">
          <span class="news-icon">动态咨询</span>
        </h2>
        
        <!-- 横向新闻布局 -->
        <div class="news-horizontal-container" v-if="newsList.length > 0">
          <div class="news-horizontal-scroll" :class="{ 'single-news': newsList.length === 1 }">
            <div class="news-horizontal-card" v-for="(news, idx) in newsList.slice(0, 3)" :key="idx" @click.stop="navigateToNewsDetail(news.id)" style="cursor: pointer;" :class="{ 'single-news-card': newsList.length === 1 }">
              <div class="news-horizontal-image" :style="{ backgroundImage: `url(${getImageUrl(news.image)})`, backgroundSize: 'cover', backgroundPosition: 'center' }"></div>
              <div class="news-horizontal-content">
                <h3 class="news-horizontal-title">{{ news.title }}</h3>
                <p class="news-horizontal-desc">{{ news.desc }}</p>
                <div class="news-horizontal-meta">
                  <span class="news-date">{{ formatDate(news.date) }}</span>
                  <span class="news-source">发布：中政量化研究院</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 无新闻时的提示 -->
        <div v-else class="no-news">
          <el-empty description="暂无新闻数据"></el-empty>
        </div>
        
        <div class="news-more">
          <el-button type="primary" @click="navigateTo('/news')">阅读更多</el-button>
        </div>
      </div>
    </section>

    <!-- 发展历程 -->
    <section class="timeline-section">
      <div class="content-wrapper">
        <TimelineSection />
      </div>
    </section>    



  

    <!-- 量化交易技术应用场景 -->
    <section class="application-scenarios">
      <div class="content-wrapper">
        <h2 class="section-title section-title-accent">量化交易技术应用场景</h2>
        <div class="scenarios-grid">
          <div class="scenario-card" v-for="(scenario, idx) in applicationScenarios" :key="idx">
            <div class="scenario-icon" :style="{ backgroundColor: scenario.iconBg, color: scenario.iconColor }">
              <i :class="scenario.icon"></i>
            </div>
            <h3 class="scenario-title">{{ scenario.title }}</h3>
            <p class="scenario-desc">{{ scenario.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 风控原则 -->
    <section class="principle-section">
      <div class="content-wrapper">
        <h2 class="section-title section-title-accent no-underline">风控原则</h2>
        <el-row :gutter="50" class="principle-row">
          <!-- 方案B: 调整为 13/11 比例，提高图片存在感 -->
          <el-col :xs="24" :md="13">
            <div class="principle-text">
              <h3 class="principle-slogan">风控优先，追求长期夏普更优</h3>
              <ul class="principle-list">
                <li><strong>多维度控制超额波动：</strong>构建丰富而低相关的因子 / 策略库，横跨频率、市场、风格，平滑短期冲击，降低净值回撤幅度与恢复时间。</li>
                <li><strong>可解释 + 快速归因：</strong>策略全链路标签化与特征字典，使异常可在分钟级拆解定位，支持实时剔除失效模块与参数漂移报警。</li>
                <li><strong>分层限额 + 动态调整：</strong>账户 / 组合 / 策略 / 单因子四层风险限额；结合波动、流动性与相关性动态收缩或释放风险预算。</li>
                <li><strong>压力测试常态化：</strong>构建宏观场景、极端跳空、相关性塌陷、流动性枯竭四类情景库，日度增量回放评估尾部损失。</li>
                <li><strong>执行与风控联动：</strong>委托拆单、滑点监测、成交偏离与盘口冲击即时回流风险引擎，防止模型收益被微结构成本侵蚀。</li>
              </ul>
            </div>
          </el-col>
          <el-col :xs="24" :md="11">
            <div class="principle-image-wrapper">
              <img :src="riskImage" alt="风险控制示意：阻断风险传递" @error="onRiskImgError" />
            </div>
          </el-col>
        </el-row>
      </div>
    </section>

    <!-- 投资理念 -->
    <section class="philosophy-section">
      <div class="content-wrapper">
        <h2 class="section-title section-title-accent">投资理念</h2>
        <div class="philosophy-grid">
          <div class="ph-item" v-for="(p, i) in philosophies" :key="i">
            <div class="ph-icon">
              <i :class="p.icon"></i>
            </div>
            <h4 class="ph-title">{{ p.title }}</h4>
            <p class="ph-desc">{{ p.desc }}</p>
          </div>
        </div>
      </div>
    </section>









    <!-- 内容详情弹窗已移除，点击直接跳转详情页 -->



  

  </div>
</template>

<script>
import { newsAPI } from '../services/api';
import TimelineSection from '../components/TimelineSection.vue';

export default {
  name: 'Home',
  components: {
    TimelineSection
  },
  data() {
    return {
  
  overviewImage: '/zhuyetupian.jpg',
  // Banner 轮播数据
  banners: [
        {
          id: 1,
          title: '中政量化技术研究院',
          summary: '专注AI、机器学习与算法大数据在多资产量化投资中的工程化落地',
          bgType: 'image',
          image: '/uploads/shan.jpg'
        },
        {
          id: 2,
          title: '全天候多策略协同',
          summary: 'Alpha / CTA / 因子轮动 / 事件驱动组合化降低相关性',
          bgType: 'image',
          image: '/uploads/city.jpg'
        }
      ],
  heroLoading: false,
  // 用户提供的实际图片 URL 兜底（与返回条目一一对应前两个）
  BANNER_FALLBACK_IMAGES: ['/shan.jpg','/city.jpg'],
  heroError: '',
      activeBanner: 0,
      autoTimer: null,
  overviewFeatures: [
        { icon: 'DataAnalysis', title: '数据驱动', desc: '多源异构数据系统化采集与清洗, 支撑高频与日频策略研发' },
        { icon: 'TrendCharts', title: '多策略协同', desc: 'Alpha / CTA / 因子轮动 / 事件驱动组合化降低相关性' },
        { icon: 'WarningFilled', title: '体系化风控', desc: '分层风险指标+实时监控+场景压力测试, 守住回撤底线' },
        { icon: 'Cpu', title: '工程化研究', desc: '统一特征仓+回测框架+自动化流水线, 缩短策略上市周期' }
      ],
      overviewStats: [
        { number: '8+', label: '核心研究成员' },
        { number: '8+', label: '平均策略方向' },
        { number: '100+', label: '在研/验证策略' },
        { number: '低相关', label: '组合相关性目标' },
        { number: '全流程', label: '数据→执行闭环' }
      ],
      
      // 新闻资讯数据
      newsList: [],
      
  riskImage: '/20191029_2.jpg',
      applicationScenarios: [
        {
          title: '风险预判模型',
          desc: '用算法大型模型判断投资产品风险，保护客户资金安全',
          icon: 'fa fa-check-circle',
          iconBg: '#e6f7ff',
          iconColor: '#1890ff'
        },
        {
          title: '股票/期货/外汇自动化交易',
          desc: '基于算法和规则的金融产品自动化交易系统，实现无人值守的交易执行',
          icon: 'fa fa-cogs',
          iconBg: '#e6f7ff',
          iconColor: '#1890ff'
        },
        {
          title: '多因子选股',
          desc: '结合多种量化因子构建选股模型，提高投资组合表现和风险调整后收益',
          icon: 'fa fa-line-chart',
          iconBg: '#e6f7ff',
          iconColor: '#1890ff'
        },
        {
          title: '风险对冲与组合优化',
          desc: '通过衍生工具和投资组合优化技术，降低系统性风险并提高收益稳定性',
          icon: 'fa fa-shield',
          iconBg: '#e6f7ff',
          iconColor: '#1890ff'
        },
        {
          title: '做市商策略',
          desc: '提供市场流动性，通过买卖价差获取收益的高频交易策略',
          icon: 'fa fa-money',
          iconBg: '#e6f7ff',
          iconColor: '#1890ff'
        },
        {
          title: '跨市场套利',
          desc: '利用不同市场间的价格差异，通过同时买入和卖出相关资产获取无风险收益',
          icon: 'fa fa-exchange',
          iconBg: '#e6f7ff',
          iconColor: '#1890ff'
        }
      ],
      philosophies: [
        { title: '数据与逻辑兼重', desc: '不迷信纯黑箱；以清晰的经济/微结构假设驱动特征设计与因子筛选，数据验证支持而非替代逻辑。', icon: 'fa fa-balance-scale' },
        { title: '多策略协同', desc: 'Alpha、趋势、统计套利、事件与结构化策略分层组合，追求低相关与收益来源多元化。', icon: 'fa fa-sitemap' },
        { title: '工程化与可复现', desc: '统一特征仓、版本化回测、参数追踪与实验记录，确保研究—上线路径一致。', icon: 'fa fa-cogs' },
        { title: '风险预算思维', desc: '以风险占用衡量资本效率，把回撤、波动、相关性、流动性成本纳入统一约束框架。', icon: 'fa fa-shield' },
        { title: '持续迭代进化', desc: '监控信号老化与因子拥挤度，自动触发再训练 / 降权 / 替换，保持策略群生命力。', icon: 'fa fa-refresh' },
        { title: '稳健优先', desc: '重视尾部与恢复期指标，高频调控暴露，力求夏普与回撤弹性兼顾。', icon: 'fa fa-line-chart' }
      ],

      quickLinks: [
        { text: '量化策略', path: '/products', icon: 'DataAnalysis' },
        { text: '研究报告', path: '/research', icon: 'Document' },
        
        { text: '关于我们', path: '/about', icon: 'InfoFilled' }
      ],

  
  // 动态来源（与 /news 页面分类同步）
  // 弹窗相关变量已移除
    }
  },
  methods: {
    formatDateTime(val){
      if(!val) return '';
      const d = new Date(val);
      if(isNaN(d.getTime())) return '';
      const pad = n => n<10? '0'+n : ''+n;
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    // 格式化日期显示
    formatDate(val) {
      if(!val) return '';
      const d = new Date(val);
      if(isNaN(d.getTime())) return '';
      const pad = n => n<10? '0'+n : ''+n;
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    },
    // 获取图片URL，处理相对路径。优先使用环境变量 BASE_URL，兼容 Vite 和 Vue CLI
    getImageUrl(image) {
      if (!image) return '';
      // 如果是完整 URL（http/https/data），直接返回
      if (typeof image === 'string' && (image.startsWith('http') || image.startsWith('data:'))) {
        return image;
      }

      // 直接使用绝对 URL 访问后端服务器的图片
      // 这样可以绕过 Nginx 配置的限制，确保图片能够正确加载
      if (typeof image === 'string' && image.startsWith('/uploads/')) {
        return image;
      }
      const path = image.startsWith("/") ? image.substring(1) : image;
      return "/uploads/" + path;
    },
    navigateTo(path) { 
      if (path === '/news') {
        this.$router.push({ path: '/news', hash: '#news-list' });
      } else {
        this.$router.push(path);
      }
    },
    navigateToNewsDetail(id) { this.$router.push(`/news/${id}`).catch(err => { if (err.name !== 'NavigationDuplicated') { console.error('导航错误:', err); } }); },
    nextBanner() { this.activeBanner = (this.activeBanner + 1) % this.banners.length },
    prevBanner() { this.activeBanner = (this.activeBanner - 1 + this.banners.length) % this.banners.length },
    gotoBanner(i) { this.activeBanner = i },
    slideStyle(b) {
      if (b.bgType === 'image' && b.image) {
        return { backgroundImage: `url(${b.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      }
      if (b.imageUrl) { // 兼容后端字段
        return { backgroundImage: `url(${b.imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      }
      return { background: b.gradient || 'linear-gradient(135deg,#0b2b4c,#061528)' }
    },
    fetchHeroBanners() {
      this.heroLoading = true;
      this.heroError = '';
      
      try {
        // 使用静态数据
        console.log('[Static Mode] Using static banners data');
        this.activeBanner = 0;
        console.log('[HeroBanner] Using static banners, length =', this.banners.length);
      } catch (e) {
        console.error('[HeroBanner] Error initializing banners', e);
        this.heroError = '横幅加载失败';
      } finally {
        this.heroLoading = false;
      }
    },
    startAuto() {
      if (this.autoTimer) return
      this.autoTimer = setInterval(() => { this.nextBanner() }, 6000)
    },
    pauseAuto() { if (this.autoTimer) { clearInterval(this.autoTimer); this.autoTimer = null } },
    resumeAuto() { this.startAuto() },
    openContent(item){
      if(!item || !item.id) return;
      // 跳转到详情页
      this.$router.push(`/contents/${encodeURIComponent(item.category || item.type || 'contents')}/${item.id}`);
    },
    onRiskImgError(){
      // 多级回退: uploads/public 根图 -> public 备用 -> 站点静态图 -> 空
      const primary = '/risk-control.jpg';
      const secondary = '/20191029_2.jpg';
      // 使用站点内现有静态图作为最终兜底，避免运行时 require 在某些构建下不可用
      const assetFallback = '/zhuyetupian.jpg';
      if(this.riskImage === primary){
        this.riskImage = secondary;
        return;
      }
      if(this.riskImage === secondary){
        this.riskImage = assetFallback;
        return;
      }
      if(this.riskImage !== assetFallback){
        this.riskImage = assetFallback;
        return;
      }
      // 最后一次仍失败则清空避免无限 onerror
      this.riskImage = '';
    },

    // 新增：获取新闻列表
    async fetchNewsList() {
      try {
        // 使用与News页面相同的数据结构，确保内容互通
        const data = await newsAPI.getLatestNews(5);
        if (data.success) {
          // 确保数据结构与News页面一致
          this.newsList = data.data.map(item => ({
            id: item.id,
            title: item.title,
            desc: item.summary || item.desc || '', // 支持summary或desc字段
            date: item.date,
            // 使用站点内静态图作为新闻兜底，避免在运行时使用 require 导致错误
            image: item.image || '/shan.jpg'
          }));
        } else {
          console.error('获取新闻列表失败:', data.message);
        }
      } catch (error) {
        console.error('获取新闻列表失败:', error);
      }
    }
  },
  mounted() {
    // 先获取轮播，再启动自动切换
    this.fetchHeroBanners();
    this.startAuto();
    // 获取新闻列表
    this.fetchNewsList();
  },
  beforeUnmount() { this.pauseAuto() }
}
</script>

<style scoped>
.home-page { min-height:100vh; background:#f5f7fa; }

/* Banner 轮播 */
.hero-slider { position:relative; height:100vh; height:100dvh; min-height:560px; overflow:hidden; z-index:1; }
.hero-slides { position:relative; width:100%; height:100%; }
.hero-slide { position:absolute; inset:0; opacity:0; transition:opacity .9s ease, transform 1.4s ease; transform:scale(1.04); display:flex; align-items:center; justify-content:center; background-size:cover; background-position:center; }
.hero-slide.is-active { opacity:1; transform:scale(1); pointer-events:auto; }
/* 暗化+径向高光叠加 */
.hero-slide::before { content:""; position:absolute; inset:0; background:linear-gradient(120deg,rgba(10,20,40,.65),rgba(10,20,40,.35)),radial-gradient(900px 520px at 70% 30%,rgba(255,255,255,.16),rgba(255,255,255,0) 70%); mix-blend-mode:normal; }
/* 轻微晶亮层 */
.hero-slide::after { content:""; position:absolute; inset:0; background:radial-gradient(1100px 560px at 25% 70%,rgba(255,255,255,.07),rgba(255,255,255,0) 65%); pointer-events:none; }
.hero-overlay { position:relative; z-index:2; max-width:900px; padding:0 46px; text-align:center; animation:heroFadeIn .9s ease forwards; opacity:0; }
@keyframes heroFadeIn { 0% { opacity:0; transform:translateY(30px); } 100% { opacity:1; transform:translateY(0); } }
.hero-banner-title { font-size:54px; font-weight:650; color:#fff; margin:0 0 18px; text-shadow:0 4px 28px rgba(0,0,0,.48); letter-spacing:1.5px; line-height:1.1; }
.hero-banner-sub { font-size:20px; line-height:1.65; color:rgba(255,255,255,.92); margin:0 0 10px; text-shadow:0 2px 14px rgba(0,0,0,.45); max-width:780px; margin-left:auto; margin-right:auto; }
@media (max-width: 1200px) { .hero-banner-title { font-size:48px; } }
@media (max-width: 992px) { .hero-slider { height:100vh; height:100dvh; min-height:520px; } .hero-banner-title { font-size:42px; } .hero-banner-sub { font-size:18px; } }
@media (max-width: 768px) { .hero-slider { height:100vh; height:100dvh; min-height:500px; } .hero-banner-title { font-size:34px; letter-spacing:.5px; } .hero-banner-sub { font-size:16px; padding:0 4px; } .hero-overlay { padding:0 90px; } .hero-arrow { width:32px; height:32px; font-size:20px; } .hero-arrow--left { left:8px; } .hero-arrow--right { right:8px; } }
@supports (height: 100dvh) { .hero-slider { height:100dvh; } }
@media (max-width: 520px) { .hero-banner-title { font-size:30px; } .hero-banner-sub { font-size:15px; } }
.hero-arrow { position:absolute; top:50%; transform:translateY(-50%); width:52px; height:52px; border:none; background:rgba(255,255,255,.18); color:#fff; border-radius:50%; display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:34px; line-height:1; backdrop-filter:blur(4px); transition:background .25s; outline:none; }
.hero-arrow:focus-visible { box-shadow:0 0 0 3px rgba(255,255,255,.55); }
.hero-arrow:hover { background:rgba(255,255,255,.32); }
.hero-arrow--left { left:34px; }
.hero-arrow--right { right:34px; }
.hero-dots { position:absolute; left:50%; bottom:38px; transform:translateX(-50%); display:flex; gap:10px; }
.hero-dots .dot { width:12px; height:12px; background:rgba(255,255,255,.4); border:none; border-radius:50%; cursor:pointer; transition:background .3s, transform .3s; }
.hero-dots .dot.active { background:#fff; transform:scale(1.15); }

/* 顶部横幅 */
.hero-inner { display:none; }
.hero-title {
  font-size: 64px;
  font-weight: 800;
  margin: 0 0 12px 0;
  letter-spacing: 1px;
}
.hero-sub {
  font-size: 20px;
  opacity: 0.85;
  margin: 0 0 28px 0;
}
.hero-cta {
  padding: 14px 28px;
  font-size: 16px;
  border-radius: 8px;
}

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-title {
  margin-bottom: 50px;
  color: #2c3e50;
  font-size: 36px;
  font-weight: 300;
  text-align: center;
}

.section-title-accent {
  position: relative;
  display: inline-block;
  margin: 0 auto;
}
.section-title-accent::after {
  content: '';
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -12px;
  width: 64px;
  height: 4px;
  background: #3a66d6;
  border-radius: 2px;
}

.section-title-accent.no-underline::after {
  display: none;
}

/* --- 已精简：量化动态 / 旧新闻 / 快速链接相关块已删除，仅保留现用模块 --- */

.intro-section {
  background: white;
  padding: 80px 0;
}

.intro-description {
  font-size: 18px;
  line-height: 1.8;
  color: #606266;
  margin-bottom: 40px;
}

.intro-image-wrap {
  width: 100%;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
}
.intro-image {
  display: block;
  width: 100%;
  height: auto;
}

/* 新版简介区域 */
.overview-section { padding: 90px 0 60px; background:#f5f7fa; }
.overview-card { background:#fff; border-radius:4px; box-shadow:0 4px 18px rgba(0,0,0,.06); overflow:hidden; }
.overview-top { display:flex; flex-direction:row; }
.overview-image { width:34%; min-height:420px; position:relative; overflow:hidden; }
.overview-image img { position:absolute; left:0; top:0; width:100%; height:100%; object-fit:cover; }
.overview-content { flex:1; padding:54px 64px 48px; background:linear-gradient(180deg,#ffffff 0%,#f8fafc 100%); }
.mini-label { font-size:14px; color:#1d5fad; margin-bottom:4px; letter-spacing:1px; }
.overview-title { font-size:28px; color:#114576; margin:0 0 14px; font-weight:600; }
.overview-underline { width:84px; height:4px; background:#1d5fad; border-radius:2px; margin-bottom:28px; }
.overview-desc { font-size:15px; line-height:1.9; color:#4c5b66; margin:0 0 32px; white-space:pre-line; }
.feature-list { display:grid; grid-template-columns:repeat(2,minmax(220px,1fr)); gap:32px 40px; margin-bottom:30px; }
.feature-item { display:flex; align-items:flex-start; gap:14px; }
.fi-icon { font-size:30px; color:#2f6fd0; line-height:1; }
.fi-text h4 { margin:0 0 6px; font-size:15px; color:#18354d; font-weight:600; }
.fi-text p { margin:0; font-size:13px; line-height:1.55; color:#5d6d79; }
.overview-cta { margin-top:6px; }
.overview-stats { display:flex; flex-wrap:wrap; justify-content:space-between; padding:38px 40px 34px; border-top:1px solid #eef1f5; background:#fff; }
.stat-item { min-width:120px; margin:0 10px 18px; }
.stat-number { font-size:30px; font-weight:600; color:#0d4e9c; letter-spacing:1px; }
.stat-label { font-size:13px; color:#5d6d79; margin-top:4px; letter-spacing:.5px; }

/* 时间轴区块 */
/* steps-section 样式已移除 */

@media (max-width: 1100px) {
  .overview-top { flex-direction:column; }
  .overview-image { width:100%; min-height:300px; }
  .overview-content { padding:40px 42px 36px; }
  .overview-stats { padding:32px 32px 24px; }
  .stat-item { min-width:100px; }
}
@media (max-width: 640px) {
  .overview-content { padding:34px 28px 30px; }
  .overview-title { font-size:24px; }
  .feature-list { grid-template-columns:repeat(2,1fr); gap:20px 18px; }

  .stat-number { font-size:24px; }
  .overview-desc { font-size:14px; }
}

@media (max-width: 520px) {
  .feature-list { grid-template-columns:1fr; }
}

/* 风控原则 */
.principle-section { background:#fff; padding:90px 0 70px; }
.principle-row { align-items:flex-start; }
.principle-slogan { font-size:34px; font-weight:500; color:#2c3e50; margin:0 0 46px; letter-spacing:1px; }
.principle-text ul { list-style:none; padding:0; margin:0; }
.principle-list li { font-size:16px; line-height:1.85; color:#34495e; margin-bottom:26px; position:relative; padding-left:1.2em; }
.principle-list li::before { content:'•'; position:absolute; left:0; top:0; color:#1b5fae; font-weight:600; }
.principle-list strong { color:#1b5fae; font-weight:600; }
.principle-image-wrapper { text-align:center; }
.principle-image-wrapper img { width:100%; height:auto; max-width:100%; border-radius:4px; box-shadow:0 6px 18px rgba(0,0,0,.08); object-fit:cover; }
@media (min-width: 992px) { .principle-image-wrapper { margin-top:108px; } }

/* 投资理念新版卡片布局 */
.philosophy-section { background:#f5f7fa; padding:90px 0 110px; }
.philosophy-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:40px 36px; align-items:stretch; }
/* 大屏固定三列，保证上下各3个（共6个）视觉平衡 */
@media (min-width: 1200px) { .philosophy-grid { grid-template-columns:repeat(3,1fr); gap:45px 40px; } }
.ph-item { background:#ffffff; border-radius:16px; padding:32px 28px 34px; position:relative; overflow:hidden; box-shadow:0 6px 20px -4px rgba(20,40,70,.08),0 2px 4px rgba(0,0,0,.04); transition:transform .45s ease, box-shadow .45s ease; }
.ph-item:before { content:""; position:absolute; inset:0; background:linear-gradient(135deg,rgba(35,110,210,.08),rgba(0,0,0,0)); opacity:.8; pointer-events:none; }
.ph-item:after { content:""; position:absolute; inset:0; border-radius:inherit; box-shadow:0 0 0 0 rgba(60,120,220,.32); opacity:0; transition:opacity .55s ease, box-shadow .55s ease; }
.ph-item:hover { transform:translateY(-8px); box-shadow:0 14px 34px -6px rgba(20,40,70,.18),0 6px 14px rgba(0,0,0,.06); }
.ph-item:hover:after { opacity:1; box-shadow:0 0 0 2px rgba(60,120,220,.28); }
.ph-icon { --size:52px; width:var(--size); height:var(--size); border-radius:50%; display:flex; align-items:center; justify-content:center; font-weight:600; font-size:20px; color:#fff; margin-bottom:20px; background:linear-gradient(145deg,#1b5fae,#3b82f6); box-shadow:0 6px 16px -4px rgba(27,95,174,.55); position:relative; letter-spacing:.5px; }
.ph-icon:after { content:""; position:absolute; inset:3px; border-radius:50%; background:radial-gradient(circle at 30% 30%,rgba(255,255,255,.65),rgba(255,255,255,0) 70%); }
.ph-title { margin:0 0 14px; font-size:20px; font-weight:600; letter-spacing:.5px; color:#193347; line-height:1.38; }
.ph-desc { margin:0; font-size:15px; line-height:1.75; color:#4d5d68; padding-top:8px; }
.ph-item:hover .ph-title { color:#154d94; }

@media (max-width: 1100px) {
  .principle-row { flex-direction:column-reverse; }
  .principle-slogan { font-size:30px; margin-bottom:40px; }
  .philosophy-grid { gap:30px 28px; }
}

@media (max-width: 768px) {
  .principle-section { padding:60px 0 50px; }
  .principle-slogan { font-size:26px; margin-bottom:32px; }
  .principle-list li { font-size:15px; line-height:1.7; margin-bottom:20px; }
  .principle-image-wrapper img { border-radius:6px; }
}

@media (max-width: 480px) {
  .principle-section { padding:50px 0 40px; }
  .principle-slogan { font-size:22px; margin-bottom:24px; }
  .principle-list li { font-size:14px; line-height:1.6; margin-bottom:16px; }
  .principle-list strong { display:inline; }
}
@media (max-width: 820px) {
  .philosophy-grid { grid-template-columns:repeat(auto-fill,minmax(250px,1fr)); gap:35px 30px; }
  .ph-item { padding:28px 24px 30px; }
  .ph-icon { --size:48px; font-size:18px; margin-bottom:16px; }
  .ph-title { font-size:18px; }
  .ph-desc { font-size:14px; }
}

@media (max-width: 640px) {
  .philosophy-grid { grid-template-columns:1fr; gap:28px; }
  .ph-item { padding:26px 22px 28px; border-radius:14px; }
  .ph-icon { --size:44px; font-size:17px; margin-bottom:14px; }
  .ph-title { font-size:17px; }
  .ph-desc { font-size:13px; line-height:1.6; }
}

@media (max-width: 520px) { 
  .philosophy-section { padding:60px 0 70px; }
  .philosophy-grid { gap:24px; }
  .ph-item { padding:22px 20px 24px; }
  .ph-icon { --size:40px; font-size:16px; }
  .ph-title { font-size:16px; }
  .ph-desc { font-size:12px; }
}

/* (已移除的旧 .news-section / .quick-links / .link-btn 样式块) */

/* 双列新闻列表 */
.dual-news-section { background:#fff; padding:60px 0 90px; }
.dual-news-block { margin-bottom:56px; }
.dual-news-title { font-size:32px; font-weight:500; color:#1b5fae; margin:0 0 26px; letter-spacing:2px; }
/* 竖排时最后一个去掉底部间距 */
.dual-news-vertical > .el-col:last-child .dual-news-block { margin-bottom:0; }
.dual-news-list { list-style:none; margin:0; padding:0; }
.dual-news-item { display:flex; justify-content:space-between; align-items:center; padding:16px 0; border-bottom:1px solid #eef1f5; cursor:pointer; gap:20px; }
.dual-news-item:last-child { border-bottom:none; }
.dual-news-text { flex:1; font-size:16px; color:#2c3e50; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.dual-news-item:hover .dual-news-text { color:#1d5fad; }
.dual-news-date { font-size:14px; color:#909399; flex-shrink:0; white-space:nowrap; min-width:140px; text-align:right; }

/* （新闻资讯聚合样式已移除） */

/* JOIN US 招募横幅 */
.join-us-section { position:relative; background:linear-gradient(135deg,#d9e2e9 0%,#cfd9e2 50%,#d9e2e9 100%); overflow:hidden; padding:140px 20px 150px; text-align:center; }
.join-us-section:before, .join-us-section:after { content:""; position:absolute; width:780px; height:780px; background:radial-gradient(circle at 30% 30%, rgba(255,255,255,.65), rgba(255,255,255,0) 70%); top:-260px; left:-260px; mix-blend-mode:overlay; }
.join-us-section:after { top:auto; bottom:-260px; left:auto; right:-220px; background:radial-gradient(circle at 70% 70%, rgba(255,255,255,.55), rgba(255,255,255,0) 70%); }
.join-us-overlay { position:absolute; inset:0; backdrop-filter:blur(2px); }
.join-us-inner { position:relative; z-index:2; max-width:1100px; margin:0 auto; }
.ju-eyebrow { font-size:40px; font-weight:600; letter-spacing:4px; color:#fff; text-shadow:0 6px 22px rgba(0,0,0,.28); margin-bottom:26px; }
.ju-title { font-size:72px; font-weight:700; line-height:1.1; margin:0 0 50px; color:#fff; letter-spacing:4px; text-shadow:0 10px 30px rgba(0,0,0,.35); }
.ju-btn { --el-button-bg-color:transparent; --el-button-border-color:#fff; --el-button-hover-bg-color:rgba(255,255,255,.12); --el-button-hover-border-color:#fff; font-size:20px; padding:16px 66px; border:2px solid #fff; background:transparent; color:#fff; letter-spacing:2px; font-weight:500; border-radius:2px; box-shadow:0 4px 18px rgba(0,0,0,.18); }
.ju-btn:hover { background:rgba(255,255,255,.12); color:#fff; }
@media (max-width: 1100px) { .ju-title { font-size:60px; } }
@media (max-width: 860px) { .ju-title { font-size:48px; } .ju-eyebrow { font-size:34px; } }
@media (max-width: 640px) { .ju-title { font-size:38px; letter-spacing:2px; } .ju-eyebrow { font-size:28px; letter-spacing:3px; margin-bottom:18px; } .join-us-section { padding:110px 20px 120px; } .ju-btn { font-size:18px; padding:14px 48px; } }

/* 量化交易技术应用场景 */
.application-scenarios {
  background: #fff;
  padding: 90px 0;
}

.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 50px;
}

.scenario-card {
  background: #fff;
  padding: 35px 30px;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-align: center;
  border: 1px solid #f0f0f0;
}

.scenario-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border-color: #e6f7ff;
}

.scenario-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 32px;
  transition: all 0.3s ease;
}

.scenario-card:hover .scenario-icon {
  transform: scale(1.1);
}

.scenario-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 12px;
}

.scenario-desc {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  margin: 0;
}

@media (max-width: 1200px) {
  .scenarios-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 25px;
  }
}

@media (max-width: 768px) {
  .application-scenarios {
    padding: 60px 0;
  }
  
  .scenarios-grid {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-top: 30px;
  }
  
  .scenario-card {
    padding: 25px 20px;
    border-radius: 6px;
  }
  
  .scenario-icon {
    width: 60px;
    height: 60px;
    font-size: 24px;
    margin-bottom: 15px;
  }
  
  .scenario-title {
    font-size: 16px;
  }
  
  .scenario-desc {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .application-scenarios {
    padding: 50px 0;
  }
  
  .scenarios-grid {
    gap: 16px;
  }
  
  .scenario-card {
    padding: 20px 16px;
  }
  
  .scenario-icon {
    width: 50px;
    height: 50px;
    font-size: 20px;
    margin-bottom: 12px;
  }
  
  .scenario-title {
    font-size: 15px;
  }
  
  .scenario-desc {
    font-size: 12px;
    line-height: 1.5;
  }
}

/* 统一合并 max-width:768px 媒体查询，减少重复规则 */
@media (max-width: 768px) {
  .dual-news-title { font-size:26px; }
  .dual-news-item { padding:12px 0; }
  .dual-news-text { font-size:14px; }
  .dual-news-date { font-size:12px; }
  .section-title { font-size:28px; }
  .hero-slider { min-height:50vh; }
  .hero-title { font-size:36px; }
  .hero-sub { font-size:16px; }
  /* 已移除旧 intro/services/news 三段统一内边距的依赖，下面保留防止未来复用 */
  .intro-section, .services-section { padding:50px 0; }
}


/* 发展历程样式 */
.timeline-section {
  background: #f5f7fa;
  padding: 80px 0;
}

.timeline-section .content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 新闻资讯样式 */
.news-section {
  background: #fff;
  padding: 60px 0;
}

.news-section .content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.news-section .section-title {
  margin-bottom: 40px;
}

.news-icon {
  font-size: 24px;
  font-weight: 500;
  color: #1b5fae;
  margin-right: 10px;
}

/* 横向新闻布局 */
.news-horizontal-container {
  margin-bottom: 30px;
  overflow: hidden;
}

.news-horizontal-scroll {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 0;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  -webkit-overflow-scrolling: touch; /* 平滑滚动 */
}

.news-horizontal-scroll.single-news {
  justify-content: center;
  overflow-x: visible;
}

.news-horizontal-scroll::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

.news-horizontal-scroll::-webkit-scrollbar-track {
  display: none; /* 隐藏滚动条轨道 */
}

.news-horizontal-scroll::-webkit-scrollbar-thumb {
  display: none; /* 隐藏滚动条滑块 */
}

.news-horizontal-card {
  flex: 0 0 350px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.news-horizontal-card.single-news-card {
  flex: 0 1 800px;
  max-width: 100%;
  display: flex;
  flex-direction: row;
  align-items: stretch;
}

.news-horizontal-card.single-news-card .news-horizontal-image {
  flex: 0 0 30%;
  height: auto;
  min-height: 220px;
}

.news-horizontal-card.single-news-card .news-horizontal-content {
  flex: 1;
  padding: 20px 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #f8fafc;
  border-left: 1px solid #e2e8f0;
}

.news-horizontal-card.single-news-card .news-horizontal-title {
  font-size: 22px;
  font-weight: 600;
  color: #114576;
  margin-bottom: 16px;
  line-height: 1.3;
}

.news-horizontal-card.single-news-card .news-horizontal-desc {
  font-size: 15px;
  color: #4c5b66;
  margin-bottom: 20px;
  line-height: 1.6;
  text-align: justify;
}

.news-horizontal-card.single-news-card .news-horizontal-meta {
  font-size: 13px;
  color: #64748b;
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.news-horizontal-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
}

.news-horizontal-image {
  height: 180px;
  background: #f0f0f0;
}

.news-horizontal-content {
  padding: 16px;
}

.news-horizontal-title {
  font-size: 17px;
  font-weight: 600;
  color: #202d50;
  margin: 0 0 10px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.news-horizontal-desc {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  margin: 0 0 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.news-horizontal-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  color: #909399;
}

/* 无新闻提示 */
.no-news {
  padding: 40px 0;
  text-align: center;
}

.news-more {
  text-align: right;
  margin-top: 24px;
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

/* 响应式设计 */
@media (max-width: 992px) {
  .news-horizontal-card {
    flex: 0 0 300px;
  }
  
  .news-horizontal-image {
    height: 160px;
  }
}

@media (max-width: 768px) {
  .news-horizontal-card {
    flex: 0 0 260px;
  }
  
  .news-horizontal-image {
    height: 140px;
  }
  
  .news-horizontal-content {
    padding: 12px;
  }
  
  .news-horizontal-title {
    font-size: 16px;
  }
  
  .news-horizontal-desc {
    font-size: 12px;
  }
  
  .news-horizontal-meta {
    font-size: 10px;
  }
}

@media (max-width: 480px) {
  .news-horizontal-container {
    margin-bottom: 24px;
  }
  
  .news-horizontal-scroll {
    gap: 12px;
    padding: 6px 0;
  }
  
  .news-horizontal-card {
    flex: 0 0 240px;
    border-radius: 6px;
  }
  
  .news-horizontal-image {
    height: 120px;
  }
  
  .news-horizontal-content {
    padding: 10px;
  }
  
  .news-horizontal-title {
    font-size: 15px;
    line-height: 1.3;
  }
  
  .news-horizontal-desc {
    font-size: 11px;
    line-height: 1.4;
  }
  
  .news-horizontal-meta {
    font-size: 9px;
  }
  
  .news-more {
    margin-top: 20px;
  }
}

@media (max-width: 992px) {
  .news-horizontal-card.single-news-card {
    flex-direction: column;
  }
  
  .news-horizontal-card.single-news-card .news-horizontal-image {
    flex: 0 0 200px;
  }
  
  .news-horizontal-card.single-news-card .news-horizontal-content {
    padding: 16px;
  }
  
  .news-horizontal-card.single-news-card .news-horizontal-title {
    font-size: 18px;
  }
}

@media (max-width: 480px) {
  .news-horizontal-card {
    flex: 0 0 220px;
  }
  
  .news-horizontal-image {
    height: 120px;
  }
  
  .news-horizontal-card.single-news-card .news-horizontal-image {
    flex: 0 0 180px;
  }
  
  .news-horizontal-card.single-news-card .news-horizontal-content {
    padding: 12px;
  }
  
  .news-horizontal-card.single-news-card .news-horizontal-title {
    font-size: 16px;
  }
  
  .news-horizontal-card.single-news-card .news-horizontal-desc {
    font-size: 13px;
  }
}
</style>